import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../Context/AuthContext';
import { UserPlus, Trash2, Shield, Mail, User as UserIcon, X, Edit2, Eye, EyeOff, Phone } from 'lucide-react';
import Swal from 'sweetalert2';
import { API_URL } from '../../config';

const Users = () => {
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    full_name: '',
    phone: '',
    password: '',
    role: 'admin'
  });

  const fetchUsers = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (err) {
      console.error('Kullanıcılar yüklenemedi:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleOpenModal = (user = null) => {
    if (user) {
      setIsEditing(true);
      setSelectedUser(user);
      setFormData({
        username: user.username,
        email: user.email,
        full_name: user.full_name || '',
        phone: user.phone || '',
        password: '', // Password stays empty on edit unless update requested
        role: user.role
      });
    } else {
      setIsEditing(false);
      setSelectedUser(null);
      setFormData({ username: '', email: '', full_name: '', phone: '', password: '', role: 'admin' });
    }
    setShowModal(true);
    setShowPassword(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const url = isEditing 
      ? `${API_URL}/admin/users/${selectedUser.id}`
      : `${API_URL}/admin/users`;
    
    const method = isEditing ? 'PUT' : 'POST';

    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (response.ok) {
        setShowModal(false);
        Swal.fire({
          icon: 'success',
          title: isEditing ? 'Güncellendi!' : 'Eklendi!',
          text: data.message || 'İşlem başarıyla tamamlandı.',
          background: '#0f172a',
          color: '#fff',
          confirmButtonColor: '#00d1ff',
          timer: 2000
        });
        fetchUsers();
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Hata!',
          text: data.message || 'Bir hata oluştu.',
          footer: data.error ? `<div style="color: #ef4444; font-size: 0.8rem; padding-top: 10px; border-top: 1px solid rgba(255,255,255,0.1); width: 100%; text-align: center;">Hata Detayı: ${data.error}</div>` : null,
          background: '#0f172a',
          color: '#fff',
          confirmButtonColor: '#00d1ff'
        });
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Bağlantı Hatası',
        text: 'Sunucuya ulaşılamadı.',
        background: '#0f172a',
        color: '#fff'
      });
    }
  };

  const handleDeleteUser = async (id) => {
    const result = await Swal.fire({
      title: 'Emin misiniz?',
      text: "Bu kullanıcıyı silmek istediğinize emin misiniz?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#334155',
      confirmButtonText: 'Evet, Sil!',
      cancelButtonText: 'İptal',
      background: '#0f172a',
      color: '#fff'
    });

    if (result.isConfirmed) {
      try {
        const response = await fetch(`${API_URL}/admin/users/${id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });

        const data = await response.json();

        if (response.ok) {
          Swal.fire({
            icon: 'success',
            title: 'Silindi!',
            text: 'Kullanıcı başarıyla silindi.',
            background: '#0f172a',
            color: '#fff',
            timer: 1500
          });
          fetchUsers();
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Hata!',
            text: data.message,
            background: '#0f172a',
            color: '#fff'
          });
        }
      } catch (err) {
        Swal.fire({
          icon: 'error',
          title: 'Hata!',
          text: 'İşlem sırasında hata oluştu.',
          background: '#0f172a',
          color: '#fff'
        });
      }
    }
  };

  if (loading) return <div className="admin-card">Yükleniyor...</div>;

  return (
    <div className="users-page">
      <div className="admin-header-flex" style={{ display: 'flex', flexDirection: window.innerWidth < 576 ? 'column' : 'row', justifyContent: 'space-between', alignItems: window.innerWidth < 576 ? 'flex-start' : 'center', marginBottom: '2rem', gap: '1.5rem' }}>
        <div>
          <h2 className="admin-page-title" style={{ fontSize: 'clamp(1.5rem, 4vw, 2.2rem)', marginBottom: '0.25rem' }}>Erişim Yetkileri</h2>
          <p style={{ color: 'var(--text-dim)', fontSize: '0.9rem' }}>Sistem yöneticilerini buradan yönetebilirsiniz.</p>
        </div>
        <button 
          className="btn-primary" 
          onClick={() => handleOpenModal()}
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.85rem 1.75rem', borderRadius: '1rem', fontWeight: '800', fontSize: '0.9rem', width: window.innerWidth < 576 ? '100%' : 'auto', textAlign: 'center', justifyContent: 'center' }}
        >
          <UserPlus size={18} />
          Yeni Kullanıcı Ekle
        </button>
      </div>

      <div className="admin-card" style={{ padding: 0, borderRadius: '1.25rem', border: '1px solid var(--navbar-border)', overflow: 'hidden' }}>
        <div className="admin-table-wrapper">
          <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse', color: 'var(--text-main)' }}>
            <thead>
              <tr style={{ background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid var(--navbar-border)' }}>
                <th style={{ textAlign: 'left', padding: '1.25rem 1.5rem', whiteSpace: 'nowrap' }}>Kullanıcı</th>
                <th style={{ textAlign: 'left', padding: '1.25rem 1.5rem', whiteSpace: 'nowrap' }}>E-posta</th>
                <th style={{ textAlign: 'left', padding: '1.25rem 1.5rem', whiteSpace: 'nowrap' }}>Telefon</th>
                <th style={{ textAlign: 'left', padding: '1.25rem 1.5rem', whiteSpace: 'nowrap' }}>Yetki Seviyesi</th>
                <th style={{ textAlign: 'left', padding: '1.25rem 1.5rem', whiteSpace: 'nowrap' }}>Kayıt</th>
                <th style={{ textAlign: 'right', padding: '1.25rem 1.5rem', whiteSpace: 'nowrap' }}>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} style={{ borderBottom: '1px solid var(--navbar-border)', background: 'transparent' }}>
                  <td style={{ padding: '1.25rem 1.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', whiteSpace: 'nowrap' }}>
                      <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: 'rgba(0, 209, 255, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(0, 209, 255, 0.2)', flexShrink: 0 }}>
                        <UserIcon size={18} color="#00d1ff" />
                      </div>
                      <div>
                          <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{u.full_name || u.username}</div>
                          <div style={{ fontSize: '0.75rem', color: '#64748b' }}>@{u.username}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '1.25rem 1.5rem', color: 'var(--text-dim)', whiteSpace: 'nowrap' }}>{u.email}</td>
                  <td style={{ padding: '1.25rem 1.5rem', color: 'var(--text-dim)', whiteSpace: 'nowrap', fontSize: '0.9rem' }}>{u.phone || 'Girilmedi'}</td>
                  <td style={{ padding: '1.25rem 1.5rem' }}>
                    <span style={{ 
                      padding: '0.35rem 0.85rem', 
                      borderRadius: '2rem', 
                      fontSize: '0.65rem', 
                      background: 'rgba(0, 209, 255, 0.05)', 
                      color: '#00d1ff',
                      border: '1px solid rgba(0, 209, 255, 0.2)',
                      fontWeight: '800',
                      textTransform: 'uppercase',
                      whiteSpace: 'nowrap'
                    }}>
                      {u.role}
                    </span>
                  </td>
                  <td style={{ padding: '1.25rem 1.5rem', color: 'var(--text-dim)', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                    {new Date(u.created_at).toLocaleDateString('tr-TR')}
                  </td>
                  <td style={{ padding: '1.25rem 1.5rem', textAlign: 'right' }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', whiteSpace: 'nowrap' }}>
                      <button 
                        onClick={() => handleOpenModal(u)}
                        style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)', color: 'white', padding: '0.4rem', borderRadius: '0.5rem', cursor: 'pointer' }}
                        title="Düzenle"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button 
                        onClick={() => handleDeleteUser(u.id)}
                        style={{ background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.1)', color: '#f87171', padding: '0.4rem', borderRadius: '0.5rem', cursor: 'pointer' }}
                        title="Sil"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal structure (Refactored to be reusable) */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.85)', backdropFilter: 'blur(12px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1.5rem' }}>
          <div className="admin-card" style={{ width: '100%', maxWidth: '480px', background: 'rgba(15, 15, 15, 0.95)', border: '1px solid rgba(0, 209, 255, 0.2)', borderRadius: '2rem', padding: '2.5rem', boxShadow: '0 0 50px rgba(0, 209, 255, 0.1)' }}>
            <button onClick={() => setShowModal(false)} style={{ position: 'absolute', right: '1.5rem', top: '1.5rem', background: 'rgba(255, 255, 255, 0.05)', border: 'none', color: '#64748b', cursor: 'pointer', width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <X size={18} />
            </button>
            <div style={{ marginBottom: '2.5rem', textAlign: 'center' }}>
              <div style={{ width: '60px', height: '60px', background: 'linear-gradient(135deg, #00d1ff 0%, #0072ff 100%)', borderRadius: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem' }}>
                <UserPlus size={28} color="white" />
              </div>
              <h3 style={{ fontSize: '1.5rem', fontWeight: '800', color: 'white' }}>{isEditing ? 'HESABI DÜZENLE' : 'YENİ HESAP EKLE'}</h3>
              <p style={{ color: '#64748b', fontSize: '0.85rem' }}>{isEditing ? 'Mevcut yönetici bilgilerini güncelleyin' : 'Erişim yetkisi olan yeni bir yönetici tanımlayın'}</p>
            </div>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#64748b' }}><UserIcon size={18} /></span>
                <input type="text" placeholder="İsim Soyisim" value={formData.full_name} onChange={e => setFormData({...formData, full_name: e.target.value})} required style={{ width: '100%', background: '#0a0a0a', border: '1px solid rgba(255, 255, 255, 0.05)', padding: '1rem 1rem 1rem 3rem', borderRadius: '1rem', color: 'white', outline: 'none' }} />
              </div>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#64748b' }}><UserIcon size={18} /></span>
                <input type="text" placeholder="Kullanıcı Adı" value={formData.username} onChange={e => setFormData({...formData, username: e.target.value})} required style={{ width: '100%', background: '#0a0a0a', border: '1px solid rgba(255, 255, 255, 0.05)', padding: '1rem 1rem 1rem 3rem', borderRadius: '1rem', color: 'white', outline: 'none' }} />
              </div>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#64748b' }}><Mail size={18} /></span>
                <input type="email" placeholder="E-posta Adresi" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} required style={{ width: '100%', background: '#0a0a0a', border: '1px solid rgba(255, 255, 255, 0.05)', padding: '1rem 1rem 1rem 3rem', borderRadius: '1rem', color: 'white', outline: 'none' }} />
              </div>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#64748b' }}><Phone size={18} /></span>
                <input type="text" placeholder="Telefon Numarası" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} required style={{ width: '100%', background: '#0a0a0a', border: '1px solid rgba(255, 255, 255, 0.05)', padding: '1rem 1rem 1rem 3rem', borderRadius: '1rem', color: 'white', outline: 'none' }} />
              </div>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#64748b' }}><Shield size={18} /></span>
                <input 
                  type={showPassword ? "text" : "password"} 
                  placeholder={isEditing ? "Şifreyi Değiştir (Opsiyonel)" : "Şifre"} 
                  value={formData.password} 
                  onChange={e => setFormData({...formData, password: e.target.value})} 
                  required={!isEditing} 
                  style={{ width: '100%', background: '#0a0a0a', border: '1px solid rgba(255, 255, 255, 0.05)', padding: '1rem 3.5rem 1rem 3rem', borderRadius: '1rem', color: 'white', outline: 'none' }} 
                />
                <button 
                  type="button" 
                  onClick={() => setShowPassword(!showPassword)}
                  style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <div style={{ marginTop: '1.5rem', display: 'flex', gap: '1rem' }}>
                <button type="button" onClick={() => setShowModal(false)} style={{ flex: 1, padding: '1rem', borderRadius: '1rem', background: 'rgba(255, 255, 255, 0.05)', color: 'white', border: '1px solid rgba(255, 255, 255, 0.1)', fontWeight: '600' }}>İptal</button>
                <button type="submit" style={{ flex: 2, padding: '1rem', borderRadius: '1rem', background: 'linear-gradient(to right, #00d1ff, #0072ff)', color: 'white', border: 'none', fontWeight: '700', boxShadow: '0 10px 20px -5px rgba(0, 209, 255, 0.4)' }}>{isEditing ? 'Güncellemeleri Kaydet' : 'Kaydet ve Yetkilendir'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};



export default Users;
